/**
 * Client-side dedup bookkeeping for due-date ESCALATION EMAILS ONLY.
 *
 * IMPORTANT DISTINCTION from `task-order-store.js`'s persisted order: the
 * escalation LEVEL a task currently shows (`getEscalationLevel()` in
 * `task-config.js`) is NEVER read from here and is NEVER persisted — it's a
 * pure function of (now, the task's current due date, its current status),
 * recomputed fresh on every render. This file exists purely to answer one
 * narrower question for the notification trigger in `TaskBoard.vue`: "have
 * we already SENT an escalation email for this task at its CURRENT due
 * date and this level (or worse)?" — so a manager who has the board open
 * for hours doesn't get re-emailed every time it re-renders.
 *
 * Keyed by `(taskId, dueDate)`, not just `taskId` — the moment a task's due
 * date changes, its old record (tied to the OLD due date) simply no longer
 * matches, so the very next check treats it as "nothing sent yet for this
 * due date" and a fresh escalation sequence starts naturally. This is what
 * satisfies "the due date can be changed" — there is no stale counter that
 * can outlive a due-date edit; changing the due date implicitly resets it.
 */

import { taskId as getTaskId } from './task-config'

const STORAGE_KEY = 'taskModuleEscalationNotified.v1'

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    return {}
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch (e) {
    console.warn('[task-escalation-store] could not persist notified level:', e)
  }
}

/**
 * The highest escalation level (its ordinal index into
 * `TASK_ESCALATION_LEVELS`) already notified for this task AT its CURRENT
 * due date — `-1` if nothing's been notified yet for this due date (either
 * truly never, or the due date changed since the last notification).
 */
export function getLastNotifiedLevelIndex(task, levels) {
  const id = getTaskId(task)
  if (id == null) return -1
  const store = readStore()
  const record = store[String(id)]
  if (!record || record.dueDate !== (task.dueDate || null)) return -1
  const idx = levels.findIndex(level => level.key === record.level)
  return idx
}

/** Record that an escalation email was just sent for this task, at this due date and level. */
export function recordNotifiedLevel(task, levelKey) {
  const id = getTaskId(task)
  if (id == null) return
  const store = readStore()
  store[String(id)] = { dueDate: task.dueDate || null, level: levelKey }
  writeStore(store)
}

/** Drop dedup state when a task is marked Done so it cannot block a fresh sequence if reopened. */
export function clearNotifiedLevel(task) {
  const id = getTaskId(task)
  if (id == null) return
  const store = readStore()
  const key = String(id)
  if (!store[key]) return
  delete store[key]
  writeStore(store)
}

export default {
  getLastNotifiedLevelIndex,
  recordNotifiedLevel,
  clearNotifiedLevel,
}
